import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    getClientEmissionCategories,
    addClientEmissionCategory,
    deleteClientEmissionCategory,
    getClientCategoryFactors,
    addClientCategoryFactor,
    deleteClientCategoryFactor,
    setClientCategoryFactorAvailability,
    getClientEmissionRecords,
    updateClientEmissionRecordFactor,
} from "../processes/client/client_emission";
import {
    searchEmissionCategories,
    searchEmissionFactors
} from "../processes/industry_category_factor/category_per_industry";

import EmissionFactorLine from "../components/EmissionFactorLine";


export default function ClientCategoryFactor({ clientId }) {
    const [activeClientId, setActiveClientId] = useState(clientId);
    const [activeCategoryId, setActiveCategoryId] = useState(null);

    return (
        <div style={{ padding: 16 }}>
            <table style={{ borderCollapse: "collapse", width: "100%", tableLayout: "fixed" }}>
                <colgroup>
                    <col style={{ width: "50%" }} />
                    <col style={{ width: "50%" }} />
                </colgroup>
                <thead>
                    <tr>
                        {/* Category & Scope header with Add button */}
                        <th style={{ ...th, position: "relative" }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                                <span>CATEGORY &amp; SCOPE</span>
                                {/* Add control lives inside Category column header */}
                                <AddCategoryControls
                                    activeClientId={activeClientId}
                                    disabled={!activeClientId}
                                />
                            </div>
                        </th>

                        <th style={{ ...th, position: "relative" }}>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                                <span>FACTOR</span>
                                <AddFactorControls
                                    clientId={activeClientId}
                                    categoryId={activeCategoryId}
                                    disabled={!activeCategoryId}
                                />
                            </div>
                        </th>
                    </tr>
                </thead>

                <tbody>
                    <tr>
                        <td style={{ ...td, overflow: "hidden" }} valign="top">
                            <CategoryList
                                clientId={activeClientId}
                                activeCategoryId={activeCategoryId}
                                onSelectCategory={(id) => setActiveCategoryId(id)}
                            />
                        </td>

                        <td style={{ ...td, overflow: "hidden" }} valign="top">
                            <FactorPanel clientId={activeClientId} categoryId={activeCategoryId} />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

/** ------------ Category list (middle column) ------------ */
function CategoryList({ clientId, activeCategoryId, onSelectCategory }) {
    const qc = useQueryClient();
    const { data: rows = [], isLoading, isError } = useQuery({
        queryKey: ["clientCategories", clientId],
        queryFn: () => getClientEmissionCategories(clientId),
        enabled: !!clientId,
    });

    const deleteMutation = useMutation({
        mutationFn: deleteClientEmissionCategory, // expects { id }
        onSuccess: () => qc.invalidateQueries({ queryKey: ["clientCategories", clientId] }),
    });

    if (isLoading) return <div>Loading categories…</div>;
    if (isError) return <div style={{ color: "crimson" }}>Failed to load categories.</div>;

    return (
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {rows.map((r) => {
                const name = r?.category?.name ?? "-";
                const scope = r?.category?.scope ?? null;
                const isActive = r.categoryId === activeCategoryId;
                return (
                    <li
                        key={r.id}
                        onClick={() => onSelectCategory(r.categoryId)}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 8,
                            border: "1px solid #e5e7eb",
                            borderRadius: 8,
                            padding: "8px 10px",
                            marginBottom: 6,
                            background: isActive ? "#eef2ff" : "#fff",
                            cursor: "pointer",
                        }}
                        title="Click to show factors"
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ fontWeight: 600 }}>{name}</div>
                            <ScopeBadge scope={scope} />
                        </div>

                        <button
                            onClick={(e) => {
                                e.stopPropagation(); // don't change selection on delete click
                                deleteMutation.mutate({ id: r.id });
                            }}
                            disabled={deleteMutation.isLoading}
                            style={{
                                padding: "4px 10px",
                                border: "1px solid #ef4444",
                                color: "#ef4444",
                                background: "#fff",
                                borderRadius: 6,
                                cursor: "pointer",
                            }}
                            title="Remove from this industry"
                        >
                            {deleteMutation.isLoading ? "Deleting…" : "Delete"}
                        </button>
                    </li>
                );
            })}
            {rows.length === 0 && (
                <li style={{ color: "#6b7280" }}>No categories yet for this industry.</li>
            )}
        </ul>
    );
}


/** ------------ Add controls in Category header ------------ */
function AddCategoryControls({ activeClientId, disabled }) {
    const qc = useQueryClient();

    const [input, setInput] = useState("");         // what user types / shows
    const [query, setQuery] = useState("");         // debounced search term
    const [open, setOpen] = useState(false);        // show/hide dropdown
    const [selected, setSelected] = useState(null); // { id, label, name, scope, nameId }

    // Debounce input -> query (250ms)
    useEffect(() => {
        const t = setTimeout(() => setQuery(input.trim()), 250);
        return () => clearTimeout(t);
    }, [input]);

    // Fetch options when open and query >= 2 chars
    const { data: options = [], isFetching } = useQuery({
        queryKey: ["emissionCategorySearch", query],
        queryFn: () => searchEmissionCategories(query),
        enabled: open && !disabled && query.length >= 2,
        staleTime: 60_000,
        keepPreviousData: true,
    });

    // Reset when industry changes
    useEffect(() => {
        setSelected(null);
        setInput("");
        setQuery("");
        setOpen(false);
    }, [activeClientId]);

    const addMutation = useMutation({
        mutationFn: addClientEmissionCategory,
        onSuccess: () => {
            if (activeClientId) {
                qc.invalidateQueries({ queryKey: ["clientCategories", activeClientId] });
            }
            setSelected(null);
            setInput("");
            setQuery("");
            setOpen(false);
        },
    });

    const canAdd = !!activeClientId && !!selected?.id && !disabled;

    return (
        <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 3 }}>
            {/* Search box */}
            <div style={{ position: "relative" }}>
                <input
                    type="text"
                    placeholder='Search category'
                    value={input}
                    onChange={(e) => {
                        setInput(e.target.value);
                        setSelected(null);   // clear previous selection when typing
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    onBlur={() => setTimeout(() => setOpen(false), 150)} // simple close after click
                    disabled={disabled}
                    style={{ padding: "6px 10px", width: 200 }}
                />
                {isFetching && (
                    <span style={{
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: 12,
                        color: "#6b7280"
                    }}>
                        …
                    </span>
                )}

                {/* Dropdown list (click to select) */}
                {open && query.length >= 2 && (
                    <div
                        style={{
                            position: "absolute",
                            zIndex: 20,
                            top: "100%",
                            left: 0,
                            right: 0,
                            background: "#fff",
                            border: "1px solid #e5e7eb",
                            borderRadius: "0 0 8px 8px",
                            maxHeight: 240,
                            overflowY: "auto",
                            boxShadow: "0 8px 24px rgba(0,0,0,.08)",
                        }}
                    >
                        {options.length === 0 && !isFetching && (
                            <div style={mutedItem}>No results</div>
                        )}

                        {options.map((opt) => (
                            <div
                                key={opt.id}
                                onMouseDown={(e) => { // mousedown so it fires before input blur
                                    e.preventDefault();
                                    setSelected(opt);
                                    setInput(opt.label);
                                    setOpen(false);
                                }}
                                style={item}
                                title={opt.label}
                            >
                                <div style={{ fontWeight: 600 }}>{opt.name || opt.label}</div>
                                <div style={{ fontSize: 12, color: "#6b7280" }}>
                                    Scope {opt.scope}{opt.nameId ? ` — ${opt.nameId}` : ""}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Add button */}
            <button
                onClick={() => canAdd && addMutation.mutate({
                    clientId: activeClientId,
                    categoryId: Number(selected.id),
                })}
                disabled={!canAdd || addMutation.isLoading}
                style={{
                    padding: "6px 10px",
                    border: "1px solid #10b981",
                    background: "#10b981",
                    color: "#fff",
                    borderRadius: 6,
                    cursor: canAdd ? "pointer" : "not-allowed",
                    fontWeight: 600,
                }}
                title={
                    disabled
                        ? "Select an industry first"
                        : selected?.label
                            ? `Add: ${selected.label}`
                            : "Choose a category"
                }
            >
                {addMutation.isLoading ? "Adding…" : "Add"}
            </button>
        </div>
    );
}

const item = {
    padding: "8px 10px",
    borderTop: "1px solid #e5e7eb",
    cursor: "pointer",
};

const mutedItem = {
    padding: "8px 10px",
    borderTop: "1px solid #e5e7eb",
    color: "#6b7280",
    fontStyle: "italic",
};

/** ------------ Small UI helpers ------------ */
function ScopeBadge({ scope }) {
    if (!scope) return <span style={{ color: "#6b7280" }}>—</span>;
    return (
        <span
            style={{
                display: "inline-block",
                padding: "2px 8px",
                borderRadius: 999,
                background: "#eef2ff",
                border: "1px solid #e5e7eb",
                fontSize: 12,
            }}
            title={`Scope ${scope}`}
        >
            Scope {scope}
        </span>
    );
}

const th = {
    textAlign: "left",
    border: "1px solid #e5e7eb",
    padding: "10px",
    background: "#f3f4f6",
    fontWeight: 700,
};
const td = {
    border: "1px solid #e5e7eb",
    padding: "10px",
    background: "#fff",
};

function FactorPanel({ clientId, categoryId }) {
    const qc = useQueryClient();
    const [migrationContext, setMigrationContext] = useState(null);
    const [recalculationContext, setRecalculationContext] = useState(null);

    const { data: factors = [], isLoading, isError } = useQuery({
        queryKey: ["clientCategoryFactors", clientId, categoryId],
        queryFn: () => getClientCategoryFactors(clientId, categoryId),
        enabled: !!clientId && !!categoryId,
    });

    const deleteMutation = useMutation({
        mutationFn: deleteClientCategoryFactor, // expects { id }
        onSuccess: () =>
            qc.invalidateQueries({ queryKey: ["clientCategoryFactors", clientId, categoryId] }),
    });

    const visibilityMutation = useMutation({
        mutationFn: setClientCategoryFactorAvailability, // expects { id, available }
        onSuccess: () =>
            qc.invalidateQueries({ queryKey: ["clientCategoryFactors", clientId, categoryId] }),
    });

    if (!categoryId) return <div style={{ color: "#6b7280" }}>Select a category to see factors.</div>;
    if (isLoading) return <div>Loading factors...</div>;
    if (isError) return <div style={{ color: "crimson" }}>Failed to load factors.</div>;

    const targetFactors = factors;

    return (
        <>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {factors.map((row) => (
                    <li
                        key={row.id}
                        style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: 8,
                            padding: "8px 10px",
                            marginBottom: 6,
                            background: "#fff",
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "baseline", gap: 8, justifyContent: "space-between" }}>
                            <div>
                                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                                    <div style={{ fontWeight: 700, opacity: row?.available === false ? 0.6 : 1 }}>{row?.factor?.name ?? "-"} ({row?.factorId})</div>
                                    <span
                                        style={{
                                            fontSize: 12,
                                            borderRadius: 999,
                                            padding: "0px 8px",
                                            border: "1px solid",
                                            borderColor: row?.isUsed ? "#fca5a5" : "#d1d5db",
                                            background: row?.isUsed ? "#fef2f2" : "#f9fafb",
                                            color: row?.isUsed ? "#b91c1c" : "#4b5563",
                                        }}
                                        title={row?.isUsed ? "This factor is already used in emission records" : "This factor has not been used in emission records"}
                                    >
                                        {row?.isUsed ? "Used" : "Not used"}
                                    </span>
                                    {row?.available === false && (
                                        <span
                                            style={{
                                                fontSize: 12,
                                                borderRadius: 999,
                                                padding: "0px 8px",
                                                border: "1px solid #d1d5db",
                                                background: "#f3f4f6",
                                                color: "#4b5563",
                                            }}
                                            title="Hidden from this client's pickers and their EF list"
                                        >
                                            Hidden
                                        </span>
                                    )}
                                </div>
                                <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.35 }}>
                                    <EmissionFactorLine data={row?.factor} yearKey="year" digits={2} />
                                </div>
                                <div>
                                    {row?.refLabel}
                                </div>
                                <div>
                                    {!!row?.units?.length && (
                                        <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                                            {row.units.map((u) => (
                                                <span key={u.id} style={{
                                                    fontSize: 12,
                                                    border: "1px solid #e5e7eb",
                                                    borderRadius: 999,
                                                    padding: "2px 8px",
                                                    background: "#f9fafb"
                                                }}>
                                                    {u.name}{u.multiplier ? ` x ${u.multiplier}` : ""}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div style={{ display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-end" }}>
                                {row?.isUsed && (
                                    <button
                                        onClick={() => {
                                            setRecalculationContext({
                                                factorId: Number(row?.factorId),
                                                factorName: row?.factor?.name ?? "-",
                                                factorVersions: Array.isArray(row?.factor?.versions) ? row.factor.versions : [],
                                                latestVersionId: row?.factor?.id ?? null,
                                            });
                                        }}
                                        style={{
                                            padding: "4px 10px",
                                            border: "1px solid #f59e0b",
                                            color: "#b45309",
                                            background: "#fff",
                                            borderRadius: 6,
                                            cursor: "pointer",
                                            maxWidth: 280,
                                            textAlign: "center",
                                            fontSize: 12,
                                        }}
                                        title="Recalculate emission records using the latest factor values"
                                    >
                                        Recalculate
                                    </button>
                                )}
                                <button
                                    onClick={() => {
                                        const nextAvailable = !(row?.available !== false);
                                        const action = nextAvailable ? "show" : "hide";
                                        const message = nextAvailable
                                            ? `Show "${row?.factor?.name ?? "-"}" again to this client? It will reappear in their EF list and data-entry pickers.`
                                            : `Hide "${row?.factor?.name ?? "-"}" from this client? It will be removed from their EF list and data-entry pickers. Existing records keep working.`;
                                        if (!window.confirm(message)) return;
                                        visibilityMutation.mutate({ id: row.id, available: nextAvailable });
                                    }}
                                    disabled={visibilityMutation.isLoading}
                                    style={{
                                        padding: "4px 10px",
                                        border: "1px solid #6b7280",
                                        color: "#374151",
                                        background: "#fff",
                                        borderRadius: 6,
                                        cursor: "pointer",
                                        maxWidth: 280,
                                        textAlign: "center",
                                        fontSize: 12,
                                    }}
                                    title={row?.available === false ? "Make this factor visible to the client again" : "Hide this factor from the client's EF list and pickers"}
                                >
                                    {visibilityMutation.isLoading
                                        ? "Saving..."
                                        : row?.available === false
                                            ? "Show"
                                            : "Hide"}
                                </button>
                                <button
                                    onClick={() => {
                                        if (row?.isUsed) {
                                            setMigrationContext({
                                                factorId: Number(row?.factorId),
                                                factorName: row?.factor?.name ?? "-",
                                            });
                                            return;
                                        }

                                        deleteMutation.mutate({ id: row.id });
                                    }}
                                    disabled={deleteMutation.isLoading}
                                    style={{
                                        padding: "4px 10px",
                                        border: row?.isUsed ? "1px solid #2563eb" : "1px solid #ef4444",
                                        color: row?.isUsed ? "#1d4ed8" : "#ef4444",
                                        background: "#fff",
                                        borderRadius: 6,
                                        cursor: "pointer",
                                        maxWidth: 280,
                                        textAlign: "center",
                                        fontSize: 12,
                                    }}
                                    title={row?.isUsed ? "Please migrate old data before deleting" : "Remove this factor"}
                                >
                                    {deleteMutation.isLoading
                                        ? "Deleting..."
                                        : row?.isUsed
                                            ? "Please migrate old data before deleting"
                                            : "Delete"}
                                </button>
                            </div>
                        </div>
                    </li>
                ))}
                {factors.length === 0 && <li style={{ color: "#6b7280" }}>No factors.</li>}
            </ul>

            <FactorMigrationModal
                open={Boolean(migrationContext)}
                onClose={() => setMigrationContext(null)}
                clientId={clientId}
                categoryId={categoryId}
                sourceFactorId={migrationContext?.factorId}
                sourceFactorName={migrationContext?.factorName}
                targetFactors={targetFactors}
                onUpdated={() => {
                    qc.invalidateQueries({ queryKey: ["clientCategoryFactors", clientId, categoryId] });
                }}
            />

            <FactorRecalculationModal
                open={Boolean(recalculationContext)}
                onClose={() => setRecalculationContext(null)}
                clientId={clientId}
                categoryId={categoryId}
                factorId={recalculationContext?.factorId}
                factorName={recalculationContext?.factorName}
                factorVersions={recalculationContext?.factorVersions ?? []}
                latestVersionId={recalculationContext?.latestVersionId ?? null}
                onUpdated={() => {
                    qc.invalidateQueries({ queryKey: ["clientCategoryFactors", clientId, categoryId] });
                }}
            />
        </>
    );
}

const FETCH_PAGE_SIZE = 2000;
const ROWS_PER_PAGE = 50;

async function fetchAllRecords({ clientId, categoryId, factorId }) {
    let all = [];
    let offset = 0;
    while (true) {
        const page = await getClientEmissionRecords({
            clientId,
            categoryId,
            factorId,
            limit: FETCH_PAGE_SIZE,
            offset,
        });
        all = all.concat(page);
        if (page.length < FETCH_PAGE_SIZE) break;
        offset += FETCH_PAGE_SIZE;
    }
    return all;
}

function FactorMigrationModal({
    open,
    onClose,
    clientId,
    categoryId,
    sourceFactorId,
    sourceFactorName,
    targetFactors = [],
    onUpdated,
}) {
    const qc = useQueryClient();
    const [selectedIds, setSelectedIds] = useState([]);
    const [targetFactorId, setTargetFactorId] = useState("");
    const [targetVersionId, setTargetVersionId] = useState("");
    const [filterYear, setFilterYear] = useState("");
    const [page, setPage] = useState(0);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const formatTotal = (value) => {
        const numericValue = Number(value);
        return Number.isFinite(numericValue) ? numericValue.toFixed(4) : "-";
    };
    const getRecordYear = (dateValue) => {
        if (!dateValue) return null;
        const parsedDate = new Date(dateValue);
        if (Number.isNaN(parsedDate.getTime())) return null;
        return String(parsedDate.getFullYear());
    };

    const { data: records = [], isLoading, isError } = useQuery({
        queryKey: ["clientEmissionRecords", clientId, categoryId, sourceFactorId],
        queryFn: () => fetchAllRecords({ clientId, categoryId, factorId: sourceFactorId }),
        enabled: open && !!clientId && !!categoryId && !!sourceFactorId,
    });

    useEffect(() => {
        if (!open) return;
        const firstTarget = targetFactors[0];
        setTargetFactorId(firstTarget ? String(firstTarget.factorId) : "");
        setFilterYear("");
        setPage(0);
    }, [open, sourceFactorId, targetFactors]);

    const selectedTargetFactor = targetFactors.find((f) => String(f.factorId) === String(targetFactorId));
    const targetFactorVersions = Array.isArray(selectedTargetFactor?.factor?.versions)
        ? selectedTargetFactor.factor.versions
        : [];
    const sortedTargetVersions = [...targetFactorVersions].sort((a, b) => {
        const yearDiff = Number(b?.year ?? 0) - Number(a?.year ?? 0);
        if (yearDiff !== 0) return yearDiff;
        return Number(b?.id ?? 0) - Number(a?.id ?? 0);
    });
    const targetLatestVersionId = selectedTargetFactor?.factor?.id ?? null;

    useEffect(() => {
        if (!open) return;
        setTargetVersionId(targetLatestVersionId ? String(targetLatestVersionId) : "");
    }, [open, targetFactorId, targetLatestVersionId]);

    useEffect(() => {
        if (!open) return;
        setSelectedIds(records.map((row) => Number(row.id)));
    }, [open, records]);

    const migrateMutation = useMutation({
        mutationFn: ({ recordIds, factorId, factorVersion }) =>
            updateClientEmissionRecordFactor({ recordIds, factorId, factorVersion }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["clientEmissionRecords", clientId, categoryId, sourceFactorId] });
            qc.invalidateQueries({ queryKey: ["clientCategoryFactors", clientId, categoryId] });
            onUpdated?.();
            onClose();
        },
    });

    if (!open) return null;

    const selectedSet = new Set(selectedIds.map(Number));
    const yearOptions = [...new Set(records.map((row) => getRecordYear(row?.date)).filter(Boolean))]
        .sort((a, b) => Number(b) - Number(a));
    const filteredRecords = filterYear
        ? records.filter((row) => getRecordYear(row?.date) === filterYear)
        : records;

    const totalPages = Math.max(1, Math.ceil(filteredRecords.length / ROWS_PER_PAGE));
    const safePage = Math.min(page, totalPages - 1);
    const pageRecords = filteredRecords.slice(safePage * ROWS_PER_PAGE, (safePage + 1) * ROWS_PER_PAGE);
    const pageRecordIds = pageRecords.map((row) => Number(row.id));
    const pageRecordIdSet = new Set(pageRecordIds);
    const pageSelectedCount = pageRecordIds.filter((id) => selectedSet.has(id)).length;
    const allPageChecked = pageRecords.length > 0 && pageSelectedCount === pageRecords.length;

    const canMigrate =
        !migrateMutation.isLoading &&
        !isLoading &&
        !isError &&
        Number(targetFactorId) > 0 &&
        selectedIds.length > 0 &&
        (sortedTargetVersions.length === 0 || Number(targetVersionId) > 0);

    const toggleRecord = (id) => {
        const numericId = Number(id);
        if (selectedSet.has(numericId)) {
            setSelectedIds((prev) => prev.filter((x) => Number(x) !== numericId));
        } else {
            setSelectedIds((prev) => [...prev, numericId]);
        }
    };

    const togglePage = () => {
        if (allPageChecked) {
            setSelectedIds((prev) => prev.filter((id) => !pageRecordIdSet.has(Number(id))));
            return;
        }
        setSelectedIds((prev) => [...new Set([...prev.map(Number), ...pageRecordIds])]);
    };

    const selectAllFiltered = () => {
        const allFilteredIds = filteredRecords.map((row) => Number(row.id));
        setSelectedIds((prev) => [...new Set([...prev.map(Number), ...allFilteredIds])]);
    };

    const clearAllFiltered = () => {
        const allFilteredIdSet = new Set(filteredRecords.map((row) => Number(row.id)));
        setSelectedIds((prev) => prev.filter((id) => !allFilteredIdSet.has(Number(id))));
    };

    const allFilteredSelected = filteredRecords.length > 0 &&
        filteredRecords.every((row) => selectedSet.has(Number(row.id)));

    return (
        <div
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.35)",
                zIndex: 1000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
            }}
            onClick={onClose}
        >
            <div
                style={{
                    width: "min(1080px, 96vw)",
                    maxHeight: "88vh",
                    overflow: "auto",
                    background: "#fff",
                    borderRadius: 12,
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 20px 40px rgba(0,0,0,0.18)",
                    padding: 16,
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 18 }}>Migrate emission records</div>
                        <div style={{ color: "#6b7280", fontSize: 13 }}>
                            Source factor: {sourceFactorName || "-"} ({sourceFactorId || "-"})
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            border: "1px solid #d1d5db",
                            background: "#fff",
                            borderRadius: 6,
                            padding: "6px 10px",
                            cursor: "pointer",
                        }}
                    >
                        Close
                    </button>
                </div>

                <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <label style={{ fontSize: 13, color: "#374151" }}>Target factor</label>
                    <select
                        value={targetFactorId}
                        onChange={(e) => setTargetFactorId(e.target.value)}
                        style={{
                            minWidth: 280,
                            padding: "6px 8px",
                            border: "1px solid #d1d5db",
                            borderRadius: 6,
                            background: "#fff",
                        }}
                    >
                        {targetFactors.length === 0 && <option value="">No available target factor</option>}
                        {targetFactors.map((f) => (
                            <option key={f.id} value={f.factorId}>
                                {f?.factor?.name ?? "-"} ({f.factorId})
                            </option>
                        ))}
                    </select>

                    <label style={{ fontSize: 13, color: "#374151" }}>Factor version</label>
                    <select
                        value={targetVersionId}
                        onChange={(e) => setTargetVersionId(e.target.value)}
                        disabled={sortedTargetVersions.length === 0}
                        style={{
                            minWidth: 200,
                            padding: "6px 8px",
                            border: "1px solid #d1d5db",
                            borderRadius: 6,
                            background: "#fff",
                        }}
                    >
                        {sortedTargetVersions.length === 0 && <option value="">No versions available</option>}
                        {sortedTargetVersions.map((v) => {
                            const isLatest = Number(v?.id) === Number(targetLatestVersionId);
                            return (
                                <option key={v.id} value={v.id}>
                                    {`v${v.id} — ${v.year ?? "-"}${isLatest ? " (latest)" : ""}`}
                                </option>
                            );
                        })}
                    </select>

                    <label style={{ fontSize: 13, color: "#374151" }}>Year</label>
                    <select
                        value={filterYear}
                        onChange={(e) => {
                            setFilterYear(e.target.value);
                            setSelectedIds([]);
                            setPage(0);
                        }}
                        style={{
                            minWidth: 120,
                            padding: "6px 8px",
                            border: "1px solid #d1d5db",
                            borderRadius: 6,
                            background: "#fff",
                        }}
                    >
                        <option value="">All years</option>
                        {yearOptions.map((year) => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </select>

                    <button
                        onClick={togglePage}
                        disabled={isLoading || isError || pageRecords.length === 0}
                        style={{
                            border: "1px solid #d1d5db",
                            background: "#fff",
                            borderRadius: 6,
                            padding: "6px 10px",
                            cursor: "pointer",
                            fontSize: 12,
                        }}
                    >
                        {allPageChecked ? "Clear page" : "Select page"}
                    </button>

                    <button
                        onClick={allFilteredSelected ? clearAllFiltered : selectAllFiltered}
                        disabled={isLoading || isError || filteredRecords.length === 0}
                        style={{
                            border: "1px solid #d1d5db",
                            background: "#fff",
                            borderRadius: 6,
                            padding: "6px 10px",
                            cursor: "pointer",
                            fontSize: 12,
                        }}
                    >
                        {allFilteredSelected ? "Clear all" : "Select all"} ({filteredRecords.length})
                    </button>

                    <span style={{ fontSize: 12, color: "#6b7280" }}>
                        Selected: {selectedIds.length} / {filteredRecords.length} total
                    </span>
                </div>

                <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                            <tr style={{ background: "#f9fafb" }}>
                                <th style={modalTh}>Select</th>
                                <th style={modalTh}>Date</th>
                                <th style={modalTh}>Asset Name</th>
                                <th style={modalTh}>Amount</th>
                                <th style={modalTh}>Emission</th>
                                <th style={modalTh}>Version</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading && (
                                <tr>
                                    <td style={modalTd} colSpan={6}>Loading records...</td>
                                </tr>
                            )}
                            {isError && (
                                <tr>
                                    <td style={{ ...modalTd, color: "crimson" }} colSpan={6}>
                                        Failed to load records.
                                    </td>
                                </tr>
                            )}
                            {!isLoading && !isError && pageRecords.length === 0 && (
                                <tr>
                                    <td style={modalTd} colSpan={6}>
                                        {filterYear ? "No records found for selected year." : "No records found for this factor."}
                                    </td>
                                </tr>
                            )}
                            {!isLoading && !isError && pageRecords.map((row) => {
                                const checked = selectedSet.has(Number(row.id));
                                return (
                                    <tr key={row.id}>
                                        <td style={modalTd}>
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => toggleRecord(row.id)}
                                            />
                                        </td>
                                        <td style={modalTd}>
                                            {row?.date
                                                ? `${new Date(row.date).getFullYear()} ${new Date(row.date).toLocaleDateString("en-US", {
                                                    month: "short",
                                                })}`
                                                : "-"}
                                        </td>
                                        <td style={modalTd}>{row.assetName || "-"}</td>
                                        <td style={modalTd}>{row?.amount ?? "-"}</td>
                                        <td style={modalTd}>{formatTotal(row?.total)}</td>
                                        <td style={modalTd}>{row?.factor?.version ?? "-"}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {!isLoading && !isError && filteredRecords.length > ROWS_PER_PAGE && (
                    <div style={{ marginTop: 8, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                        <button
                            onClick={() => setPage(0)}
                            disabled={safePage === 0}
                            style={paginationBtn}
                        >
                            &laquo;
                        </button>
                        <button
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={safePage === 0}
                            style={paginationBtn}
                        >
                            &lsaquo; Prev
                        </button>
                        <span style={{ fontSize: 12, color: "#374151" }}>
                            Page {safePage + 1} of {totalPages}
                        </span>
                        <button
                            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                            disabled={safePage >= totalPages - 1}
                            style={paginationBtn}
                        >
                            Next &rsaquo;
                        </button>
                        <button
                            onClick={() => setPage(totalPages - 1)}
                            disabled={safePage >= totalPages - 1}
                            style={paginationBtn}
                        >
                            &raquo;
                        </button>
                    </div>
                )}

                {migrateMutation.isError && (
                    <div style={{ color: "crimson", marginTop: 10, fontSize: 13 }}>
                        {migrateMutation.error?.response?.data?.message || "Migration failed."}
                    </div>
                )}

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
                    <button
                        onClick={onClose}
                        style={{
                            border: "1px solid #d1d5db",
                            background: "#fff",
                            borderRadius: 6,
                            padding: "6px 10px",
                            cursor: "pointer",
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => {
                            if (!canMigrate) return;
                            setConfirmOpen(true);
                        }}
                        disabled={!canMigrate}
                        style={{
                            border: "1px solid #2563eb",
                            background: "#2563eb",
                            color: "#fff",
                            borderRadius: 6,
                            padding: "6px 12px",
                            cursor: canMigrate ? "pointer" : "not-allowed",
                            opacity: canMigrate ? 1 : 0.5,
                            fontWeight: 600,
                        }}
                    >
                        {migrateMutation.isLoading ? "Migrating..." : "Migrate selected records"}
                    </button>
                </div>

                {confirmOpen && (
                    <div
                        style={{
                            position: "fixed",
                            inset: 0,
                            background: "rgba(0,0,0,0.35)",
                            zIndex: 1200,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 16,
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setConfirmOpen(false);
                        }}
                    >
                        <div
                            style={{
                                width: "min(420px, 92vw)",
                                background: "#fff",
                                borderRadius: 10,
                                border: "1px solid #e5e7eb",
                                boxShadow: "0 20px 40px rgba(0,0,0,0.18)",
                                padding: 16,
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>
                                Confirm migration
                            </div>
                            <div style={{ marginTop: 8, fontSize: 13, color: "#4b5563", lineHeight: 1.4 }}>
                                Migrate {selectedIds.length} selected record(s) to factor {targetFactorId}
                                {targetVersionId ? ` (version v${targetVersionId})` : ""}?
                            </div>
                            <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end", gap: 8 }}>
                                <button
                                    onClick={() => setConfirmOpen(false)}
                                    style={{
                                        border: "1px solid #d1d5db",
                                        background: "#fff",
                                        borderRadius: 6,
                                        padding: "6px 10px",
                                        cursor: "pointer",
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        if (!canMigrate) {
                                            setConfirmOpen(false);
                                            return;
                                        }
                                        migrateMutation.mutate({
                                            recordIds: selectedIds,
                                            factorId: Number(targetFactorId),
                                            factorVersion: targetVersionId ? Number(targetVersionId) : undefined,
                                        });
                                        setConfirmOpen(false);
                                    }}
                                    style={{
                                        border: "1px solid #2563eb",
                                        background: "#2563eb",
                                        color: "#fff",
                                        borderRadius: 6,
                                        padding: "6px 10px",
                                        cursor: "pointer",
                                        fontWeight: 600,
                                    }}
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function FactorRecalculationModal({
    open,
    onClose,
    clientId,
    categoryId,
    factorId,
    factorName,
    factorVersions = [],
    latestVersionId = null,
    onUpdated,
}) {
    const qc = useQueryClient();
    const [selectedIds, setSelectedIds] = useState([]);
    const [filterYear, setFilterYear] = useState("");
    const [page, setPage] = useState(0);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [selectedVersionId, setSelectedVersionId] = useState("");
    const formatTotal = (value) => {
        const numericValue = Number(value);
        return Number.isFinite(numericValue) ? numericValue.toFixed(4) : "-";
    };
    const getRecordYear = (dateValue) => {
        if (!dateValue) return null;
        const parsedDate = new Date(dateValue);
        if (Number.isNaN(parsedDate.getTime())) return null;
        return String(parsedDate.getFullYear());
    };

    const { data: records = [], isLoading, isError } = useQuery({
        queryKey: ["clientEmissionRecordsRecalc", clientId, categoryId, factorId],
        queryFn: () => fetchAllRecords({ clientId, categoryId, factorId }),
        enabled: open && !!clientId && !!categoryId && !!factorId,
    });

    useEffect(() => {
        if (!open) return;
        setFilterYear("");
        setPage(0);
        setSelectedVersionId(latestVersionId ? String(latestVersionId) : "");
    }, [open, factorId, latestVersionId]);

    useEffect(() => {
        if (!open) return;
        setSelectedIds(records.map((row) => Number(row.id)));
    }, [open, records]);

    const recalculateMutation = useMutation({
        mutationFn: ({ recordIds, factorId, factorVersion }) =>
            updateClientEmissionRecordFactor({ recordIds, factorId, factorVersion }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["clientEmissionRecordsRecalc", clientId, categoryId, factorId] });
            qc.invalidateQueries({ queryKey: ["clientCategoryFactors", clientId, categoryId] });
            onUpdated?.();
            onClose();
        },
    });

    const sortedVersions = [...factorVersions].sort((a, b) => {
        const yearDiff = Number(b?.year ?? 0) - Number(a?.year ?? 0);
        if (yearDiff !== 0) return yearDiff;
        return Number(b?.id ?? 0) - Number(a?.id ?? 0);
    });

    if (!open) return null;

    const selectedSet = new Set(selectedIds.map(Number));
    const yearOptions = [...new Set(records.map((row) => getRecordYear(row?.date)).filter(Boolean))]
        .sort((a, b) => Number(b) - Number(a));
    const filteredRecords = filterYear
        ? records.filter((row) => getRecordYear(row?.date) === filterYear)
        : records;

    const totalPages = Math.max(1, Math.ceil(filteredRecords.length / ROWS_PER_PAGE));
    const safePage = Math.min(page, totalPages - 1);
    const pageRecords = filteredRecords.slice(safePage * ROWS_PER_PAGE, (safePage + 1) * ROWS_PER_PAGE);
    const pageRecordIds = pageRecords.map((row) => Number(row.id));
    const pageRecordIdSet = new Set(pageRecordIds);
    const pageSelectedCount = pageRecordIds.filter((id) => selectedSet.has(id)).length;
    const allPageChecked = pageRecords.length > 0 && pageSelectedCount === pageRecords.length;

    const canRecalculate =
        !recalculateMutation.isLoading &&
        !isLoading &&
        !isError &&
        selectedIds.length > 0 &&
        (sortedVersions.length === 0 || Number(selectedVersionId) > 0);

    const toggleRecord = (id) => {
        const numericId = Number(id);
        if (selectedSet.has(numericId)) {
            setSelectedIds((prev) => prev.filter((x) => Number(x) !== numericId));
        } else {
            setSelectedIds((prev) => [...prev, numericId]);
        }
    };

    const togglePage = () => {
        if (allPageChecked) {
            setSelectedIds((prev) => prev.filter((id) => !pageRecordIdSet.has(Number(id))));
            return;
        }
        setSelectedIds((prev) => [...new Set([...prev.map(Number), ...pageRecordIds])]);
    };

    const selectAllFiltered = () => {
        const allFilteredIds = filteredRecords.map((row) => Number(row.id));
        setSelectedIds((prev) => [...new Set([...prev.map(Number), ...allFilteredIds])]);
    };

    const clearAllFiltered = () => {
        const allFilteredIdSet = new Set(filteredRecords.map((row) => Number(row.id)));
        setSelectedIds((prev) => prev.filter((id) => !allFilteredIdSet.has(Number(id))));
    };

    const allFilteredSelected = filteredRecords.length > 0 &&
        filteredRecords.every((row) => selectedSet.has(Number(row.id)));

    return (
        <div
            style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.35)",
                zIndex: 1000,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
            }}
            onClick={onClose}
        >
            <div
                style={{
                    width: "min(1080px, 96vw)",
                    maxHeight: "88vh",
                    overflow: "auto",
                    background: "#fff",
                    borderRadius: 12,
                    border: "1px solid #e5e7eb",
                    boxShadow: "0 20px 40px rgba(0,0,0,0.18)",
                    padding: 16,
                }}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                    <div>
                        <div style={{ fontWeight: 700, fontSize: 18 }}>Recalculate emission records</div>
                        <div style={{ color: "#6b7280", fontSize: 13 }}>
                            Factor: {factorName || "-"} ({factorId || "-"})
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            border: "1px solid #d1d5db",
                            background: "#fff",
                            borderRadius: 6,
                            padding: "6px 10px",
                            cursor: "pointer",
                        }}
                    >
                        Close
                    </button>
                </div>

                <div style={{ marginTop: 12, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                    <label style={{ fontSize: 13, color: "#374151" }}>Factor version</label>
                    <select
                        value={selectedVersionId}
                        onChange={(e) => setSelectedVersionId(e.target.value)}
                        disabled={sortedVersions.length === 0}
                        style={{
                            minWidth: 200,
                            padding: "6px 8px",
                            border: "1px solid #d1d5db",
                            borderRadius: 6,
                            background: "#fff",
                        }}
                    >
                        {sortedVersions.length === 0 && <option value="">No versions available</option>}
                        {sortedVersions.map((v) => {
                            const isLatest = Number(v?.id) === Number(latestVersionId);
                            return (
                                <option key={v.id} value={v.id}>
                                    {`v${v.id} — ${v.year ?? "-"}${isLatest ? " (latest)" : ""}`}
                                </option>
                            );
                        })}
                    </select>

                    <label style={{ fontSize: 13, color: "#374151" }}>Year</label>
                    <select
                        value={filterYear}
                        onChange={(e) => {
                            setFilterYear(e.target.value);
                            setSelectedIds([]);
                            setPage(0);
                        }}
                        style={{
                            minWidth: 120,
                            padding: "6px 8px",
                            border: "1px solid #d1d5db",
                            borderRadius: 6,
                            background: "#fff",
                        }}
                    >
                        <option value="">All years</option>
                        {yearOptions.map((year) => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </select>

                    <button
                        onClick={togglePage}
                        disabled={isLoading || isError || pageRecords.length === 0}
                        style={{
                            border: "1px solid #d1d5db",
                            background: "#fff",
                            borderRadius: 6,
                            padding: "6px 10px",
                            cursor: "pointer",
                            fontSize: 12,
                        }}
                    >
                        {allPageChecked ? "Clear page" : "Select page"}
                    </button>

                    <button
                        onClick={allFilteredSelected ? clearAllFiltered : selectAllFiltered}
                        disabled={isLoading || isError || filteredRecords.length === 0}
                        style={{
                            border: "1px solid #d1d5db",
                            background: "#fff",
                            borderRadius: 6,
                            padding: "6px 10px",
                            cursor: "pointer",
                            fontSize: 12,
                        }}
                    >
                        {allFilteredSelected ? "Clear all" : "Select all"} ({filteredRecords.length})
                    </button>

                    <span style={{ fontSize: 12, color: "#6b7280" }}>
                        Selected: {selectedIds.length} / {filteredRecords.length} total
                    </span>
                </div>

                <div style={{ marginTop: 12, border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                            <tr style={{ background: "#f9fafb" }}>
                                <th style={modalTh}>Select</th>
                                <th style={modalTh}>Date</th>
                                <th style={modalTh}>Asset Name</th>
                                <th style={modalTh}>Amount</th>
                                <th style={modalTh}>Emission</th>
                                <th style={modalTh}>Version</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading && (
                                <tr>
                                    <td style={modalTd} colSpan={6}>Loading records...</td>
                                </tr>
                            )}
                            {isError && (
                                <tr>
                                    <td style={{ ...modalTd, color: "crimson" }} colSpan={6}>
                                        Failed to load records.
                                    </td>
                                </tr>
                            )}
                            {!isLoading && !isError && pageRecords.length === 0 && (
                                <tr>
                                    <td style={modalTd} colSpan={6}>
                                        {filterYear ? "No records found for selected year." : "No records found for this factor."}
                                    </td>
                                </tr>
                            )}
                            {!isLoading && !isError && pageRecords.map((row) => {
                                const checked = selectedSet.has(Number(row.id));
                                return (
                                    <tr key={row.id}>
                                        <td style={modalTd}>
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => toggleRecord(row.id)}
                                            />
                                        </td>
                                        <td style={modalTd}>
                                            {row?.date
                                                ? `${new Date(row.date).getFullYear()} ${new Date(row.date).toLocaleDateString("en-US", {
                                                    month: "short",
                                                })}`
                                                : "-"}
                                        </td>
                                        <td style={modalTd}>{row.assetName || "-"}</td>
                                        <td style={modalTd}>{row?.amount ?? "-"}</td>
                                        <td style={modalTd}>{formatTotal(row?.total)}</td>
                                        <td style={modalTd}>{row?.factor?.version ?? "-"}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {!isLoading && !isError && filteredRecords.length > ROWS_PER_PAGE && (
                    <div style={{ marginTop: 8, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                        <button
                            onClick={() => setPage(0)}
                            disabled={safePage === 0}
                            style={paginationBtn}
                        >
                            &laquo;
                        </button>
                        <button
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={safePage === 0}
                            style={paginationBtn}
                        >
                            &lsaquo; Prev
                        </button>
                        <span style={{ fontSize: 12, color: "#374151" }}>
                            Page {safePage + 1} of {totalPages}
                        </span>
                        <button
                            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                            disabled={safePage >= totalPages - 1}
                            style={paginationBtn}
                        >
                            Next &rsaquo;
                        </button>
                        <button
                            onClick={() => setPage(totalPages - 1)}
                            disabled={safePage >= totalPages - 1}
                            style={paginationBtn}
                        >
                            &raquo;
                        </button>
                    </div>
                )}

                {recalculateMutation.isError && (
                    <div style={{ color: "crimson", marginTop: 10, fontSize: 13 }}>
                        {recalculateMutation.error?.response?.data?.message || "Recalculation failed."}
                    </div>
                )}

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
                    <button
                        onClick={onClose}
                        style={{
                            border: "1px solid #d1d5db",
                            background: "#fff",
                            borderRadius: 6,
                            padding: "6px 10px",
                            cursor: "pointer",
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => {
                            if (!canRecalculate) return;
                            setConfirmOpen(true);
                        }}
                        disabled={!canRecalculate}
                        style={{
                            border: "1px solid #f59e0b",
                            background: "#f59e0b",
                            color: "#fff",
                            borderRadius: 6,
                            padding: "6px 12px",
                            cursor: canRecalculate ? "pointer" : "not-allowed",
                            opacity: canRecalculate ? 1 : 0.5,
                            fontWeight: 600,
                        }}
                    >
                        {recalculateMutation.isLoading ? "Recalculating..." : "Recalculate selected records"}
                    </button>
                </div>

                {confirmOpen && (
                    <div
                        style={{
                            position: "fixed",
                            inset: 0,
                            background: "rgba(0,0,0,0.35)",
                            zIndex: 1200,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: 16,
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setConfirmOpen(false);
                        }}
                    >
                        <div
                            style={{
                                width: "min(420px, 92vw)",
                                background: "#fff",
                                borderRadius: 10,
                                border: "1px solid #e5e7eb",
                                boxShadow: "0 20px 40px rgba(0,0,0,0.18)",
                                padding: 16,
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>
                                Confirm recalculation
                            </div>
                            <div style={{ marginTop: 8, fontSize: 13, color: "#4b5563", lineHeight: 1.4 }}>
                                Recalculate {selectedIds.length} selected record(s) for factor {factorName} ({factorId})
                                {selectedVersionId ? ` using version v${selectedVersionId}` : ""}?
                            </div>
                            <div style={{ marginTop: 14, display: "flex", justifyContent: "flex-end", gap: 8 }}>
                                <button
                                    onClick={() => setConfirmOpen(false)}
                                    style={{
                                        border: "1px solid #d1d5db",
                                        background: "#fff",
                                        borderRadius: 6,
                                        padding: "6px 10px",
                                        cursor: "pointer",
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => {
                                        if (!canRecalculate) {
                                            setConfirmOpen(false);
                                            return;
                                        }
                                        recalculateMutation.mutate({
                                            recordIds: selectedIds,
                                            factorId: Number(factorId),
                                            factorVersion: selectedVersionId ? Number(selectedVersionId) : undefined,
                                        });
                                        setConfirmOpen(false);
                                    }}
                                    style={{
                                        border: "1px solid #f59e0b",
                                        background: "#f59e0b",
                                        color: "#fff",
                                        borderRadius: 6,
                                        padding: "6px 10px",
                                        cursor: "pointer",
                                        fontWeight: 600,
                                    }}
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

const paginationBtn = {
    border: "1px solid #d1d5db",
    background: "#fff",
    borderRadius: 6,
    padding: "4px 10px",
    cursor: "pointer",
    fontSize: 12,
};

const modalTh = {
    borderBottom: "1px solid #e5e7eb",
    textAlign: "left",
    padding: "8px 10px",
    fontSize: 12,
};

const modalTd = {
    borderBottom: "1px solid #f1f5f9",
    padding: "8px 10px",
    fontSize: 12,
};

function AddFactorControls({ clientId, categoryId, disabled }) {
    const qc = useQueryClient();

    const [input, setInput] = useState("");
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState(null);

    // debounce input -> query
    useEffect(() => {
        const t = setTimeout(() => setQuery(input.trim()), 250);
        return () => clearTimeout(t);
    }, [input]);

    // fetch results only when open & query length ok
    const { data: options = [], isFetching } = useQuery({
        queryKey: ["emissionFactorSearch", query],
        queryFn: () => searchEmissionFactors(query),
        enabled: open && !disabled && query.length >= 2,
        staleTime: 60_000,
        keepPreviousData: true,
    });

    // reset when category changes
    useEffect(() => {
        setInput("");
        setQuery("");
        setSelected(null);
        setOpen(false);
    }, [categoryId]);

    const addMutation = useMutation({
        mutationFn: addClientCategoryFactor,
        onSuccess: () => {
            if (categoryId) {
                qc.invalidateQueries({ queryKey: ["clientCategoryFactors", clientId, categoryId] });
            }
            setInput("");
            setQuery("");
            setSelected(null);
            setOpen(false);
        },
    });

    const canAdd = !!categoryId && !!selected?.id && !disabled;

    return (
        <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ position: "relative" }}>
                <input
                    type="text"
                    placeholder="Search factor"
                    value={input}
                    onChange={(e) => {
                        setInput(e.target.value);
                        setSelected(null);
                        setOpen(true);
                    }}
                    onFocus={() => setOpen(true)}
                    onBlur={() => setTimeout(() => setOpen(false), 150)}
                    disabled={disabled}
                    style={{ padding: "6px 10px", width: 280 }}
                />
                {isFetching && (
                    <span style={{
                        position: "absolute",
                        right: 8,
                        top: "50%",
                        transform: "translateY(-50%)",
                        fontSize: 12,
                        color: "#6b7280"
                    }}>
                        …
                    </span>
                )}

                {open && query.length >= 2 && (
                    <div
                        style={{
                            position: "absolute",
                            zIndex: 20,
                            top: "100%",
                            left: 0,
                            right: 0,
                            background: "#fff",
                            border: "1px solid #e5e7eb",
                            borderRadius: "0 0 8px 8px",
                            maxHeight: 240,
                            overflowY: "auto",
                            boxShadow: "0 8px 24px rgba(0,0,0,.08)",
                        }}
                    >
                        {options.length === 0 && !isFetching && (
                            <div style={mutedItem}>No results</div>
                        )}

                        {options.map((opt) => (
                            <div
                                key={`${opt.id}-${opt.label}`}
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    setSelected(opt);
                                    setInput(opt.label);
                                    setOpen(false);
                                }}
                                style={item}
                                title={opt.label}
                            >
                                <div style={{ fontWeight: 600 }}>{opt.name} ({opt.id})</div>
                                <div style={{ fontSize: 12, color: "#6b7280" }}>
                                    <EmissionFactorLine data={opt?.factor} yearKey="year" digits={2} />
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <button
                onClick={() => canAdd && addMutation.mutate({ clientId, categoryId, factorId: Number(selected.id) })}
                disabled={!canAdd || addMutation.isLoading}
                style={{
                    padding: "6px 10px",
                    border: "1px solid #10b981",
                    background: "#10b981",
                    color: "#fff",
                    borderRadius: 6,
                    cursor: canAdd ? "pointer" : "not-allowed",
                    fontWeight: 600,
                }}
                title={
                    disabled
                        ? "Select a category first"
                        : selected?.label
                            ? `Add: ${selected.label}`
                            : "Choose a factor"
                }
            >
                {addMutation.isLoading ? "Adding…" : "Add"}
            </button>
        </div>
    );
}



